import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';
import type { NormalizedPoint, DrawingLine } from '../model';
import type { DrawingAtom } from '../domain/graphDrawing';
import type { EditorSelection, EditorTool } from '../domain/editor';
import { ESCAPE_PRIORITY, useEscapeAction } from './escapeArbiter';
import {
  isArrow,
  type PaperAnchor,
  type PaperArrow,
  type RenderableAnnotation,
  type RenderableArrow
} from '../domain/annotationRendering';
import type { PaperSize } from '../domain/geometry';
import { normalizedToPixel, distanceBetween } from '../domain/bonds';
import { type ViewportTransform } from '../domain/viewport';
import { selectionAtPoint, usesTouchTarget, CLICK_MOVEMENT_TOLERANCE_PX } from '../domain/paperHitTesting';
import { arrowHandlePoints, defaultArrowControls, editableArrow, type ArrowHandle } from '../domain/arrowEditing';
import type { ReactionArrowAnnotationKind, ReactionArrowOverlay } from '../chemistry/reactionParticipant';
import { NO_SNAP, snappedDragOffset, type SnapPoint } from '../domain/gridSnap';

type Event = ReactPointerEvent<HTMLDivElement>;
interface Drag {
  pointerId: number;
  element: HTMLDivElement;
  start: NormalizedPoint;
  original: PaperArrow;
  arrow: PaperArrow;
  handle?: ArrowHandle;
}
interface Options {
  tool: EditorTool;
  annotations: readonly RenderableAnnotation[];
  atoms: readonly DrawingAtom[];
  lines: readonly DrawingLine[];
  selection: EditorSelection;
  onSelectionChange: (selection: EditorSelection) => void;
  onArrowChange: (arrow: PaperArrow) => void;
  sizeRef: RefObject<PaperSize>;
  viewportRef: RefObject<ViewportTransform>;
  documentId?: string;
  // #772 part 2b -- Snap to grid (identity when off); attaching to an atom or bond wins.
  snapPoint?: SnapPoint;
}

export function usePaperArrows({
  tool,
  annotations,
  atoms,
  lines,
  selection,
  onSelectionChange,
  onArrowChange,
  sizeRef,
  viewportRef,
  documentId,
  snapPoint = NO_SNAP
}: Options) {
  const dragRef = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<PaperArrow | null>(null);
  const armed = tool.startsWith('curved-arrow-') || tool.startsWith('reaction-arrow-');
  const point = (event: Event): NormalizedPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewport = viewportRef.current;
    return {
      x: (event.clientX - rect.left - viewport.panX) / viewport.zoom / sizeRef.current.width,
      y: (event.clientY - rect.top - viewport.panY) / viewport.zoom / sizeRef.current.height
    };
  };
  const resolve = (anchor: PaperAnchor): NormalizedPoint => {
    if (anchor.kind === 'point') return anchor.at;
    if (anchor.kind === 'bond') {
      const line = lines.find((line) => line.id === anchor.bondId);
      return line ? { x: (line.start.x + line.end.x) / 2, y: (line.start.y + line.end.y) / 2 } : { x: 0, y: 0 };
    }
    return atoms.find((atom) => atom.id === anchor.atomId)?.position ?? { x: 0, y: 0 };
  };
  const anchor = (at: NormalizedPoint, event: Event, arrowId?: string): PaperAnchor => {
    const hit = selectionAtPoint(
      normalizedToPixel(at, sizeRef.current),
      atoms,
      lines,
      sizeRef.current,
      (usesTouchTarget(event.pointerType) ? 22 : 10) / viewportRef.current.zoom
    );
    if (hit?.kind === 'atom') return { kind: 'atom', atomId: hit.id };
    if (hit?.kind === 'bond') return { kind: 'bond', bondId: hit.id };
    // A free end lands on the grid, unless Shift is holding it to a 15-degree angle.
    return {
      kind: 'point',
      at: event.shiftKey
        ? at
        : snapPoint(at, { altKey: event.altKey, ...(arrowId ? { exclude: new Set([arrowId]) } : {}) })
    };
  };
  const project = (arrow: PaperArrow): RenderableArrow => {
    const common = {
      id: arrow.id,
      from: resolve(arrow.from),
      to: resolve(arrow.to),
      fromAnchor: arrow.from,
      toAnchor: arrow.to,
      ...arrow.style
    };
    return arrow.kind === 'curved-arrow'
      ? {
          ...common,
          kind: arrow.kind,
          head: arrow.head,
          bow: arrow.bow ?? 0,
          ...(arrow.controls ? { controls: arrow.controls } : {})
        }
      : {
          ...common,
          kind: arrow.kind,
          arrowKind: arrow.arrowKind,
          ...(arrow.overlay ? { overlay: arrow.overlay } : {})
        };
  };
  const cancel = useCallback(() => {
    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (drag?.element.hasPointerCapture(drag.pointerId)) drag.element.releasePointerCapture(drag.pointerId);
  }, []);
  useEffect(() => {
    // Cancel native capture when the tool, document, or committed geometry changes.
    // eslint-disable-next-line react/set-state-in-effect
    cancel();
    return cancel;
  }, [tool, documentId, annotations, cancel]);
  // #762 -- Escape cancels an unfinished arrow drag (escapeArbiter).
  useEscapeAction(ESCAPE_PRIORITY.gesture, () => {
    if (!dragRef.current) return false;
    cancel();
    return true;
  });

  const updated = (drag: Drag, event: Event): PaperArrow => {
    const at = point(event);
    if (drag.handle === 'move') {
      const rawX = at.x - drag.start.x,
        rawY = at.y - drag.start.y;
      if (
        Math.hypot(rawX * sizeRef.current.width, rawY * sizeRef.current.height) * viewportRef.current.zoom <
        CLICK_MOVEMENT_TOLERANCE_PX
      )
        return drag.original;
      // The tail lands on the grid; the rest of the arrow moves with it.
      const { x: dx, y: dy } = snappedDragOffset(resolve(drag.original.from), { x: rawX, y: rawY }, snapPoint, {
        altKey: event.altKey,
        exclude: new Set([drag.original.id])
      });
      const move = (p: NormalizedPoint) => ({ x: p.x + dx, y: p.y + dy });
      return {
        ...drag.original,
        from: { kind: 'point', at: move(resolve(drag.original.from)) },
        to: { kind: 'point', at: move(resolve(drag.original.to)) },
        ...(drag.original.kind === 'curved-arrow' && drag.original.controls
          ? { controls: drag.original.controls.map(move) as [NormalizedPoint, NormalizedPoint] }
          : {})
      };
    }
    if (drag.handle?.startsWith('control-') && drag.original.kind === 'curved-arrow' && drag.original.controls) {
      const controls: [NormalizedPoint, NormalizedPoint] = [...drag.original.controls];
      controls[drag.handle === 'control-0' ? 0 : 1] = snapPoint(at, {
        altKey: event.altKey,
        exclude: new Set([drag.original.id])
      });
      return { ...drag.original, controls };
    }
    const endpoint = drag.handle === 'from' ? 'from' : 'to';
    const fixed = resolve(drag.original[endpoint === 'from' ? 'to' : 'from']);
    let target = at;
    if (
      !drag.handle &&
      distanceBetween(normalizedToPixel(drag.start, sizeRef.current), normalizedToPixel(at, sizeRef.current)) *
        viewportRef.current.zoom <
        CLICK_MOVEMENT_TOLERANCE_PX
    )
      return drag.original;
    if (event.shiftKey) {
      const a = normalizedToPixel(fixed, sizeRef.current);
      const b = normalizedToPixel(at, sizeRef.current);
      const length = distanceBetween(a, b);
      const angle = (Math.round(Math.atan2(b.y - a.y, b.x - a.x) / (Math.PI / 12)) * Math.PI) / 12;
      target = {
        x: (a.x + Math.cos(angle) * length) / sizeRef.current.width,
        y: (a.y + Math.sin(angle) * length) / sizeRef.current.height
      };
    }
    const next = { ...drag.original, [endpoint]: anchor(target, event, drag.original.id) };
    const from = resolve(next.from);
    const to = resolve(next.to);
    // Snapping both ends to the same atom must not produce a zero-length arrow.
    if (
      distanceBetween(normalizedToPixel(from, sizeRef.current), normalizedToPixel(to, sizeRef.current)) *
        viewportRef.current.zoom <
      CLICK_MOVEMENT_TOLERANCE_PX
    )
      return drag.original;
    if (next.kind === 'curved-arrow') {
      if (!drag.handle) return { ...next, controls: defaultArrowControls(from, to, sizeRef.current) };
      if (next.controls) {
        const controls: [NormalizedPoint, NormalizedPoint] = [...next.controls];
        const index = endpoint === 'from' ? 0 : 1;
        const old = resolve(drag.original[endpoint]);
        const moved = endpoint === 'from' ? from : to;
        controls[index] = { x: controls[index].x + moved.x - old.x, y: controls[index].y + moved.y - old.y };
        return { ...next, controls };
      }
    }
    return next;
  };
  const begin = (event: Event, arrow: PaperArrow, handle?: ArrowHandle) => {
    event.preventDefault();
    dragRef.current = {
      pointerId: event.pointerId,
      element: event.currentTarget,
      start: point(event),
      original: arrow,
      arrow,
      ...(handle ? { handle } : {})
    };
    setPreview(arrow);
    onSelectionChange({ atomIds: [], bondIds: [], annotationIds: [arrow.id] });
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const pointerDown = (event: Event): boolean => {
    if (event.button !== 0 || (!armed && tool !== 'select')) return false;
    if (dragRef.current) return true;
    const pointer = normalizedToPixel(point(event), sizeRef.current);
    const radius = (usesTouchTarget(event.pointerType) ? 22 : 10) / viewportRef.current.zoom;
    const graphHit = tool === 'select' ? selectionAtPoint(pointer, atoms, lines, sizeRef.current, radius) : null;
    // A selected atom owns its drag even when an arrow is anchored at the same point.
    if (graphHit?.kind === 'atom' && selection.atomIds.includes(graphHit.id)) return false;
    for (const annotation of annotations) {
      if (!isArrow(annotation) || !selection.annotationIds?.includes(annotation.id)) continue;
      const hit = arrowHandlePoints(annotation, sizeRef.current).find(
        ({ point }) => distanceBetween(pointer, normalizedToPixel(point, sizeRef.current)) <= radius
      );
      if (hit) {
        begin(event, editableArrow(annotation, sizeRef.current), hit.handle);
        return true;
      }
    }
    if (graphHit) return false;
    // Geometry hit-testing follows the rendered paths, including wavy shafts and open heads.
    const elements = [
      ...event.currentTarget.querySelectorAll<SVGGeometryElement>(
        '.annotation-layer g[data-annotation-id] > path, .annotation-layer g[data-annotation-id] > polyline, .annotation-layer g[data-annotation-id] > polygon'
      )
    ].reverse();
    for (const element of elements) {
      const bounds = element.getBBox();
      if (
        pointer.x < bounds.x - radius ||
        pointer.x > bounds.x + bounds.width + radius ||
        pointer.y < bounds.y - radius ||
        pointer.y > bounds.y + bounds.height + radius
      )
        continue;
      const length = element.getTotalLength();
      const steps = Math.max(1, Math.min(1024, Math.ceil(length / (radius / 2))));
      let hit = false;
      for (let i = 0; i <= steps; i++) {
        if (distanceBetween(pointer, element.getPointAtLength((length * i) / steps)) <= radius) {
          hit = true;
          break;
        }
      }
      if (hit) {
        const id = element.parentElement?.getAttribute('data-annotation-id');
        if (id) {
          const arrow = annotations.find((a) => a.id === id);
          if (arrow && isArrow(arrow)) begin(event, editableArrow(arrow, sizeRef.current), 'move');
          return true;
        }
      }
    }
    if (!armed) return false;
    const from = anchor(point(event), event);
    const origin = resolve(from);
    const vertical = tool.includes('vertical-');
    const length = 90;
    const to: PaperAnchor = {
      kind: 'point',
      at: {
        x:
          origin.x +
          (vertical
            ? 0
            : (origin.x * sizeRef.current.width + length > sizeRef.current.width ? -length : length) /
              sizeRef.current.width),
        y:
          origin.y +
          (vertical ? (origin.y * sizeRef.current.height < length ? length : -length) / sizeRef.current.height : 0)
      }
    };
    const id = globalThis.crypto.randomUUID();
    let arrow: PaperArrow;
    if (tool.startsWith('curved-arrow-')) {
      arrow = {
        id,
        kind: 'curved-arrow',
        head: tool.slice('curved-arrow-'.length) as 'full' | 'half' | 'multi',
        from,
        to,
        controls: defaultArrowControls(origin, to.at, sizeRef.current)
      };
    } else {
      const [kind, overlay] = tool.slice('reaction-arrow-'.length).split('.');
      arrow = {
        id,
        kind: 'reaction-arrow',
        arrowKind: kind as ReactionArrowAnnotationKind,
        ...(overlay ? { overlay: overlay as ReactionArrowOverlay } : {}),
        from,
        to
      };
    }
    begin(event, arrow);
    return true;
  };
  const pointerMove = (event: Event): boolean => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (drag.pointerId !== event.pointerId) return true;
    event.preventDefault();
    drag.arrow = updated(drag, event);
    setPreview(drag.arrow);
    return true;
  };
  const finish = (event: Event, cancelled: boolean): boolean => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (drag.pointerId !== event.pointerId) return true;
    const next = cancelled ? null : updated(drag, event);
    cancel();
    if (next && (!drag.handle || JSON.stringify(next) !== JSON.stringify(drag.original))) onArrowChange(next);
    return true;
  };
  // Endpoint references also track the transient atom/bond positions during structure drags.
  const anchoredAnnotations = annotations.map((annotation) =>
    !isArrow(annotation)
      ? annotation
      : {
          ...annotation,
          from: annotation.fromAnchor ? resolve(annotation.fromAnchor) : annotation.from,
          to: annotation.toAnchor ? resolve(annotation.toAnchor) : annotation.to
        }
  );
  const renderedAnnotations = preview
    ? [...anchoredAnnotations.filter((annotation) => annotation.id !== preview.id), project(preview)]
    : anchoredAnnotations;
  // #933: while an arrow is drawn or its end dragged, the atoms and bonds its ends snap to.
  const snapTargets: readonly NormalizedPoint[] = preview
    ? [preview.from, preview.to].filter((end) => end.kind !== 'point').map(resolve)
    : [];
  return { pointerDown, pointerMove, finish, cancel, renderedAnnotations, snapTargets };
}
